import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import {
  apiRoute as api,
  CgaApiType,
  CghApiType,
  ConditionsAnimationsType,
  ConditionsHebergementType,
  DirectusFile,
  DirectusFileFk,
  fetchJson,
} from '../helpers/ApiHelper';
import { getCompleteLocale } from '../helpers/LangHelper';

type SummaryProps = {
  title: string;
  description?: string;
  file?: DirectusFile;
  files?: DirectusFileFk[];
};

function SummaryBlock({ title, description, files, file }: SummaryProps) {
  const { t } = useTranslation('', { keyPrefix: 'CGA' });

  return (
    <div className="spec">
      <details>
        <summary>
          <strong>{title}</strong>
          <span className="detail">{t('details')}</span>
        </summary>
        <div className="summary-content">
          {description && <p>{description}</p>}
          {files &&
            files.map((fkFile) => (
              <p key={fkFile.id}>
                <Link target="_blank" to={`${api}/assets/${fkFile.directus_files_id.id}`}>
                  {fkFile.directus_files_id.title}
                </Link>
              </p>
            ))}

          {file && (
            <p>
              <Link target="_blank" to={`${api}/assets/${file.id}`}>
                {file.title}
              </Link>
            </p>
          )}
        </div>
      </details>
    </div>
  );
}

export default function Cga() {
  const { t, i18n } = useTranslation('', { keyPrefix: 'CGA' });

  const [apiCgh, setCgh] = useState<ConditionsHebergementType>();
  const [apiCga, setCga] = useState<ConditionsAnimationsType>();
  const [isCgLoading, setCgLoading] = useState<boolean>(false);

  useEffect(() => {
    setCgLoading(true);

    const cgh = fetchJson<{ data: CghApiType }>(
      `${api}/items/ConditionsHebergement?fields=translations.*.*.*`
    )
      .then((json) => {
        setCgh(
          json.data.translations.find(
            (trans: ConditionsHebergementType) =>
              trans.languages_code.code === getCompleteLocale(i18n.resolvedLanguage)
          )
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(err);
      });

    const cga = fetchJson<{ data: CgaApiType }>(
      `${api}/items/ConditionsAnimations?fields=translations.*.*.*`
    )
      .then((json) => {
        setCga(
          json.data.translations.find(
            (trans: ConditionsAnimationsType) =>
              trans.languages_code.code === getCompleteLocale(i18n.resolvedLanguage)
          )
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(err);
      });

    Promise.all([cgh, cga]).finally(() => setCgLoading(false));
  }, [i18n.language]);

  return (
    <main id="cga">
      <Hero page="CGA" imgPath="../assets/layouts/team-cover.jpg" panorama="panorama-light-green" />
      {/* CGH */}
      <section className="container tight">
        <h3>{t('sections.cgh')}</h3>
        {isCgLoading && (
          <p>
            <i>Chargement des documents en cours...</i>
          </p>
        )}
        <div className="subsections">
          {apiCgh && (
            <>
              <SummaryBlock
                title={apiCgh.A_title}
                description={apiCgh.A_description}
                files={apiCgh.A_Conditions}
              />

              <SummaryBlock
                title={apiCgh.B_title}
                description={apiCgh.B_description}
                file={apiCgh?.B1_NuitesMinimumParSejour}
              />

              <SummaryBlock
                title={apiCgh.C_title}
                description={apiCgh.C_description}
                file={apiCgh?.C1_ConditionDAnnulation}
              />

              <SummaryBlock
                title={apiCgh.D_title}
                description={apiCgh.D_description}
                files={apiCgh?.D_Nettoyages}
              />
            </>
          )}
        </div>
      </section>

      {/* CGA */}

      <section className="container tight">
        <h3>{t('sections.cga')}</h3>
        {isCgLoading && (
          <p>
            <i>Chargement des documents en cours...</i>
          </p>
        )}
        <div className="subsections">
          {apiCga && (
            <>
              <SummaryBlock
                title={apiCga.E_title}
                description={apiCga.E_description}
                files={apiCga.E_Packs}
              />

              <SummaryBlock
                title={apiCga.F_title}
                description={apiCga.F_description}
                files={apiCga.F_Animations}
              />

              <SummaryBlock
                title={apiCga.G_title}
                description={apiCga.G_description}
                files={apiCga.G_Location}
              />
            </>
          )}
        </div>
      </section>
    </main>
  );
}
